"use client";
import { useFocusable } from "../focusable/focusable.js";
import { DialogDescriptionContext, DialogHeadingContext, DialogScopedContextProvider, useDialogProviderContext } from "./dialog-context.js";
import { isHidden, useDisclosureContent } from "../disclosure/disclosure-content.js";
import { useFocusableContainer } from "../focusable/focusable-container.js";
import { HeadingLevel } from "../heading/heading-level.js";
import { usePortal } from "../portal/portal.js";
import { isElementMarked, markTreeInside } from "./utils/tree-cleanup.js";
import { createWalkTreeSnapshot } from "./utils/walk-tree-outside.js";
import { markTreeOutside } from "./utils/mark-tree-outside.js";
import { DialogBackdrop } from "./dialog-backdrop.js";
import { useDialogStore } from "./dialog-store.js";
import { supportsInert } from "./utils/supports-inert.js";
import { disableTree, markAndDisableTreeOutside } from "./utils/disable-tree.js";
import { prependHiddenDismiss } from "./utils/prepend-hidden-dismiss.js";
import { useHideOnInteractOutside } from "./utils/use-hide-on-interact-outside.js";
import { useNestedDialogs } from "./utils/use-nested-dialogs.js";
import { usePreventBodyScroll } from "./utils/use-prevent-body-scroll.js";
import { createElement, createHook, forwardRef, useBooleanEvent, useEvent, useId, useMergeRefs, usePortalRef, useSafeLayoutEffect, useWrapElement } from "@ariakit/react-utils";
import { addGlobalEventListener, chain, contains, getActiveElement, getDocument, getFirstTabbableIn, getWindow, isElement, isFocusable, isNode, isSafari } from "@ariakit/utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { Fragment as Fragment$1, jsx, jsxs } from "react/jsx-runtime";
import { useStoreState } from "@ariakit/react-store";
import { sync } from "@ariakit/store";
//#region src/dialog/dialog.tsx
const TagName = "div";
const isSafariBrowser = isSafari();
function isAlreadyFocusingAnotherElement(dialog) {
	const activeElement = getActiveElement(dialog);
	if (!activeElement) return false;
	if (dialog && contains(dialog, activeElement)) return false;
	if (isFocusable(activeElement)) return true;
	return false;
}
function getElementFromProp(prop, focusable = false) {
	if (!prop) return null;
	const element = "current" in prop ? prop.current : prop;
	if (!element) return null;
	if (focusable) return isFocusable(element) ? element : null;
	return element;
}
/**
* Returns props to create a `Dialog` component.
* @see https://ariakit.com/components/dialog
* @example
* ```jsx
* const store = useDialogStore();
* const props = useDialog({ store });
* <Role {...props}>Dialog</Role>
* ```
*/
const useDialog = createHook(function useDialog({ store: storeProp, open: openProp, onClose, focusable = true, modal = true, portal = modal, backdrop = modal, hideOnEscape = true, hideOnInteractOutside = true, getPersistentElements, preventBodyScroll = modal, autoFocusOnShow = true, autoFocusOnHide = true, initialFocus, finalFocus, unmountOnHide, unstable_treeSnapshotKey, ...props }) {
	const context = useDialogProviderContext();
	const ref = useRef(null);
	const backdropRef = useRef(null);
	const store = useDialogStore({
		store: storeProp || context,
		open: openProp,
		setOpen(open) {
			if (open) return;
			const dialog = ref.current;
			if (!dialog) return;
			const event = new Event("close", {
				bubbles: false,
				cancelable: true
			});
			if (onClose) dialog.addEventListener("close", onClose, { once: true });
			dialog.dispatchEvent(event);
			if (!event.defaultPrevented) return;
			store.setOpen(true);
		}
	});
	const { portalRef, domReady } = usePortalRef(portal, props.portalRef);
	const preserveTabOrderProp = props.preserveTabOrder;
	const preserveTabOrder = useStoreState(store, (state) => preserveTabOrderProp && !modal && state.mounted);
	const id = useId(props.id);
	const open = useStoreState(store, "open");
	const mounted = useStoreState(store, "mounted");
	const contentElement = useStoreState(store, "contentElement");
	const hidden = isHidden(mounted, props.hidden, props.alwaysVisible);
	usePreventBodyScroll(contentElement, id, preventBodyScroll && !hidden);
	const interactedOutsideRef = useRef(false);
	useSafeLayoutEffect(() => {
		return sync(store, ["open"], (state) => {
			if (!state.open) return;
			interactedOutsideRef.current = false;
		});
	}, [store]);
	useHideOnInteractOutside(store, hideOnInteractOutside, domReady, interactedOutsideRef);
	const { wrapElement, nestedDialogs } = useNestedDialogs(store);
	props = useWrapElement(props, wrapElement, [wrapElement]);
	const lastMousedownRef = useRef(null);
	if (isSafariBrowser) useEffect(() => {
		if (!domReady) return;
		const dialog = ref.current;
		if (!dialog) return;
		const doc = getDocument(dialog);
		const onMousedown = (event) => {
			lastMousedownRef.current = event.target;
		};
		doc.addEventListener("mousedown", onMousedown, true);
		return () => {
			doc.removeEventListener("mousedown", onMousedown, true);
		};
	}, [domReady]);
	useSafeLayoutEffect(() => {
		if (!open) return;
		const dialog = ref.current;
		const activeElement = getActiveElement(dialog, true);
		if (!activeElement) return;
		if (activeElement.tagName === "BODY") {
			const fallback = lastMousedownRef.current;
			lastMousedownRef.current = null;
			if (!fallback?.isConnected) return;
			if (!isFocusable(fallback)) return;
			if (dialog && contains(dialog, fallback)) return;
			store.setDisclosureElement(fallback);
			return;
		}
		if (dialog && contains(dialog, activeElement)) return;
		store.setDisclosureElement(activeElement);
	}, [store, open]);
	useEffect(() => {
		if (!mounted) return;
		if (!domReady) return;
		const dialog = ref.current;
		if (!dialog) return;
		const win = getWindow(dialog);
		const viewport = win.visualViewport || win;
		const setViewportHeight = () => {
			const height = win.visualViewport?.height ?? win.innerHeight;
			dialog.style.setProperty("--dialog-viewport-height", `${height}px`);
		};
		setViewportHeight();
		viewport.addEventListener("resize", setViewportHeight);
		return () => {
			viewport.removeEventListener("resize", setViewportHeight);
		};
	}, [mounted, domReady]);
	useEffect(() => {
		if (!modal) return;
		if (!mounted) return;
		if (!domReady) return;
		const dialog = ref.current;
		if (!dialog) return;
		if (dialog.querySelector("[data-dialog-dismiss]")) return;
		return prependHiddenDismiss(dialog, store.hide);
	}, [
		store,
		modal,
		mounted,
		domReady
	]);
	useSafeLayoutEffect(() => {
		if (!supportsInert()) return;
		if (open) return;
		if (!mounted) return;
		if (!domReady) return;
		const dialog = ref.current;
		if (!dialog) return;
		return disableTree(dialog);
	}, [
		open,
		mounted,
		domReady
	]);
	const canTakeTreeSnapshot = open && domReady;
	useSafeLayoutEffect(() => {
		if (!id) return;
		if (!canTakeTreeSnapshot) return;
		const dialog = ref.current;
		return createWalkTreeSnapshot(id, [dialog]);
	}, [
		id,
		canTakeTreeSnapshot,
		unstable_treeSnapshotKey
	]);
	const getPersistentElementsProp = useEvent(getPersistentElements);
	useSafeLayoutEffect(() => {
		if (!id) return;
		if (!canTakeTreeSnapshot) return;
		const { disclosureElement } = store.getState();
		const allElements = [
			ref.current,
			...getPersistentElementsProp() || [],
			...nestedDialogs.map((dialog) => dialog.getState().contentElement)
		];
		const restoreInsideMarks = markTreeInside(id, allElements);
		if (modal) return chain(restoreInsideMarks, markAndDisableTreeOutside(id, allElements));
		return chain(restoreInsideMarks, markTreeOutside(id, [disclosureElement, ...allElements]));
	}, [
		id,
		store,
		canTakeTreeSnapshot,
		getPersistentElementsProp,
		nestedDialogs,
		modal,
		unstable_treeSnapshotKey
	]);
	const mayAutoFocusOnShow = !!autoFocusOnShow;
	const autoFocusOnShowProp = useBooleanEvent(autoFocusOnShow);
	const [autoFocusEnabled, setAutoFocusEnabled] = useState(false);
	useEffect(() => {
		if (!open) return;
		if (!mayAutoFocusOnShow) return;
		if (!domReady) return;
		if (!contentElement?.isConnected) return;
		const element = getElementFromProp(initialFocus, true) || contentElement.querySelector("[data-autofocus=true],[autofocus]") || getFirstTabbableIn(contentElement, true, portal && preserveTabOrder) || contentElement;
		const isElementFocusable = isFocusable(element);
		if (!autoFocusOnShowProp(isElementFocusable ? element : null)) return;
		setAutoFocusEnabled(true);
		queueMicrotask(() => {
			if (!store.getState().open) return;
			element.focus();
			if (!isSafariBrowser) return;
			if (!isElementFocusable) return;
			element.scrollIntoView({
				block: "nearest",
				inline: "nearest"
			});
		});
	}, [
		open,
		mayAutoFocusOnShow,
		domReady,
		contentElement,
		initialFocus,
		portal,
		preserveTabOrder,
		store,
		autoFocusOnShowProp
	]);
	const mayAutoFocusOnHide = !!autoFocusOnHide;
	const autoFocusOnHideProp = useBooleanEvent(autoFocusOnHide);
	const [hasOpened, setHasOpened] = useState(false);
	useSafeLayoutEffect(() => {
		if (!open) return;
		setHasOpened(true);
		return () => setHasOpened(false);
	}, [open]);
	const focusOnHide = useCallback((dialog, retry = true) => {
		if (interactedOutsideRef.current) return;
		const { disclosureElement } = store.getState();
		if (isAlreadyFocusingAnotherElement(dialog)) return;
		let element = getElementFromProp(finalFocus) || disclosureElement;
		if (element?.id) {
			const doc = getDocument(element);
			const selector = `[aria-activedescendant="${element.id}"]`;
			const composite = doc.querySelector(selector);
			if (composite) element = composite;
		}
		if (element && !isFocusable(element)) {
			const maybeParentDialog = element.closest("[data-dialog]");
			if (maybeParentDialog?.id) {
				const doc = getDocument(maybeParentDialog);
				const selector = `[aria-controls~="${maybeParentDialog.id}"]`;
				const control = doc.querySelector(selector);
				if (control) element = control;
			}
		}
		const isElementFocusable = element && isFocusable(element);
		if (!isElementFocusable && retry) {
			requestAnimationFrame(() => focusOnHide(dialog, false));
			return;
		}
		if (!autoFocusOnHideProp(isElementFocusable ? element : null)) return;
		if (!isElementFocusable) return;
		element?.focus();
	}, [
		store,
		finalFocus,
		autoFocusOnHideProp
	]);
	const focusedOnHideRef = useRef(false);
	useSafeLayoutEffect(() => {
		if (open) return;
		if (!hasOpened) return;
		if (!mayAutoFocusOnHide) return;
		const dialog = ref.current;
		focusedOnHideRef.current = true;
		focusOnHide(dialog);
	}, [
		open,
		hasOpened,
		domReady,
		mayAutoFocusOnHide,
		focusOnHide
	]);
	useEffect(() => {
		if (!hasOpened) return;
		if (!mayAutoFocusOnHide) return;
		const dialog = ref.current;
		return () => {
			if (focusedOnHideRef.current) {
				focusedOnHideRef.current = false;
				return;
			}
			focusOnHide(dialog);
		};
	}, [
		hasOpened,
		mayAutoFocusOnHide,
		focusOnHide
	]);
	const hideOnEscapeProp = useBooleanEvent(hideOnEscape);
	useEffect(() => {
		if (!domReady) return;
		if (!mounted) return;
		const onKeyDown = (event) => {
			if (event.key !== "Escape") return;
			if (event.defaultPrevented) return;
			const dialog = ref.current;
			if (!dialog) return;
			if (isElementMarked(dialog)) return;
			const target = event.target;
			if (!isNode(target)) return;
			const { disclosureElement } = store.getState();
			const isValidTarget = () => {
				if (isElement(target) && target.tagName === "BODY") return true;
				if (contains(dialog, target)) return true;
				if (!disclosureElement) return true;
				if (contains(disclosureElement, target)) return true;
				if (isElement(target) && isElementMarked(target, dialog.id)) return true;
				return false;
			};
			if (!isValidTarget()) return;
			if (!hideOnEscapeProp(event)) return;
			store.hide();
		};
		return addGlobalEventListener("keydown", onKeyDown, true, contentElement ? getWindow(contentElement) : void 0);
	}, [
		store,
		domReady,
		mounted,
		contentElement,
		hideOnEscapeProp
	]);
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(HeadingLevel, {
		level: modal ? 1 : void 0,
		children: element
	}), [modal]);
	const hiddenProp = props.hidden;
	const alwaysVisible = props.alwaysVisible;
	props = useWrapElement(props, (element) => {
		if (!backdrop) return element;
		return /* @__PURE__ */ jsxs(Fragment$1, { children: [/* @__PURE__ */ jsx(DialogBackdrop, {
			store,
			backdrop,
			backdropRef,
			hidden: hiddenProp,
			alwaysVisible
		}), element] });
	}, [
		store,
		backdrop,
		hiddenProp,
		alwaysVisible
	]);
	const [headingId, setHeadingId] = useState();
	const [descriptionId, setDescriptionId] = useState();
	props = useWrapElement(props, (element) => /* @__PURE__ */ jsx(DialogScopedContextProvider, {
		value: store,
		children: /* @__PURE__ */ jsx(DialogHeadingContext.Provider, {
			value: setHeadingId,
			children: /* @__PURE__ */ jsx(DialogDescriptionContext.Provider, {
				value: setDescriptionId,
				children: element
			})
		})
	}), [store]);
	props = {
		"data-dialog": "",
		role: "dialog",
		tabIndex: focusable ? -1 : void 0,
		"aria-labelledby": props["aria-label"] != null ? void 0 : headingId,
		"aria-describedby": descriptionId,
		...props,
		id,
		ref: useMergeRefs(ref, props.ref)
	};
	props = useFocusableContainer({
		...props,
		autoFocusOnShow: autoFocusEnabled
	});
	props = useDisclosureContent({
		store,
		...props,
		unstable_otherElementRef: backdropRef
	});
	props = useFocusable({
		...props,
		focusable
	});
	props = usePortal({
		portal,
		...props,
		portalRef,
		preserveTabOrder
	});
	return props;
});
function createDialogComponent(Component, useProviderContext = useDialogProviderContext) {
	return forwardRef(function DialogComponent(props) {
		const context = useProviderContext();
		if (!useStoreState(props.store || context, (state) => !props.unmountOnHide || state?.mounted || !!props.open)) return null;
		return /* @__PURE__ */ jsx(Component, { ...props });
	});
}
const DialogWithStore = createDialogComponent(forwardRef(function DialogImpl(props) {
	return createElement(TagName, useDialog(props));
}), useDialogProviderContext);
const DialogWithInternalStore = forwardRef(function DialogWithInternalStore(props) {
	const store = useDialogStore({ open: props.open });
	return /* @__PURE__ */ jsx(DialogWithStore, {
		...props,
		store
	});
});
/**
* Renders a dialog similar to the native `dialog` element that's rendered in a
* [`portal`](https://ariakit.com/reference/dialog#portal) by default.
*
* The dialog can be either
* [`modal`](https://ariakit.com/reference/dialog#modal) or non-modal. The
* visibility state can be controlled with the
* [`open`](https://ariakit.com/reference/dialog#open) and
* [`onClose`](https://ariakit.com/reference/dialog#onclose) props.
* @see https://ariakit.com/components/dialog
* @example
* ```jsx {4-6}
* const [open, setOpen] = useState(false);
*
* <button onClick={() => setOpen(true)}>Open dialog</button>
* <Dialog open={open} onClose={() => setOpen(false)}>
*   Dialog
* </Dialog>
* ```
*/
const Dialog = forwardRef(function Dialog(props) {
	const context = useDialogProviderContext();
	if (props.store || context || !props.unmountOnHide) return /* @__PURE__ */ jsx(DialogWithStore, { ...props });
	return /* @__PURE__ */ jsx(DialogWithInternalStore, { ...props });
});
//#endregion
export { Dialog, createDialogComponent, useDialog };

//# sourceMappingURL=dialog.js.map